import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { handleLoginNavigate } from "../functions/auth/handleLoginNavigate";
import { loginUser } from "../redux/thunks/authThunks.js";
import { useTheme } from "../../places/context/ThemeContext.jsx";

export default function useLoginPatient({ redirectTo } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const parseAuthError = useCallback(() => {
    return {
      email: "",
      password: "",
      general: "Login failed: Email or password incorrect !",
    };
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const trimmedEmail = email.trim();

    if (!trimmedEmail) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail))
      newErrors.email = "Invalid email format";

    if (!password) newErrors.password = "Password is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, password]);

  const handleLogin = useCallback(async () => {
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    setErrors({});

    try {
      sessionStorage.removeItem("patientTwoFactorChallenge");
      const data = await dispatch(
        loginUser({ email: email.trim(), password })
      ).unwrap();

      setTheme("light");

      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else {
        handleLoginNavigate(data, navigate, email.trim());
      }
    } catch (err) {
      setErrors(parseAuthError());
    } finally {
      setSubmitting(false);
    }
  }, [
    dispatch,
    email,
    password,
    submitting,
    validateForm,
    navigate,
    setTheme,
    parseAuthError,
    redirectTo,
  ]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") handleLogin();
    },
    [handleLogin]
  );

  return {
    email,
    password,
    submitting,
    errors,
    setEmail,
    setPassword,
    setErrors,
    handleLogin,
    onKeyDown,
  };
}